// Configuration for document detail fields and sections
import { User } from "../../../contexts/TenancyContext";
import { getMenuOptions } from "@/services/menusService";

export interface DocumentFieldConfig {
  key: string;
  label: string;
  type?: 'text' | 'select' | 'tags' | 'date' | 'textarea' | 'url' | 'controlcombobox' | 'singleuser' | 'multiuser';
  options?: Array<{ key: string; value: string }> | string[];
  metadata?: Record<string, any>;
  section: string;
  fullWidth?: boolean;
  readOnly?: boolean;
  getOptionLabel?: (value: string) => string;
}

// Section layout configuration
export const sectionConfigs: Record<string, { itemsPerRow: number }> = {
  'Document Information': { itemsPerRow: 4 },
  'Classification & References': { itemsPerRow: 4 },
  'Document Content': { itemsPerRow: 2 },
  'Document Metadata': { itemsPerRow: 3 },
};

// Helper to ensure options are in the correct format
const normalizeOptions = (options: Array<{key: string; value: string}>) => {
  // Return options as an array of just the values
  return options.map(opt => opt.value);
};

export async function generateFieldConfig(users: User[] = [], companyId?: string): Promise<DocumentFieldConfig[]> {
  // Fetch all menu options in parallel (excluding statuses since it's no longer configurable)
  const [
    documentTypes,
    classifications,
    businessUnits,
  ] = await Promise.all([
    getMenuOptions('documentLibrary', 'documentTypes', companyId),
    getMenuOptions('documentLibrary', 'classifications', companyId),
    getMenuOptions('documentLibrary', 'businessUnits', companyId),
    // getMenuOptions('documentLibrary', 'documentCategories', companyId),
  ]);

  console.log('[documentLibrary] documentTypes:', documentTypes);
  console.log('[documentLibrary] classifications:', classifications);
  console.log('[documentLibrary] businessUnits:', businessUnits);
  // console.log('[documentLibrary] documentCategories:', documentCategories);

  // Static status options since statuses are no longer configurable in menu settings
  // These match the output values from convertStatus function
  const statuses = [
    { key: 'Draft', value: 'Draft' },
    { key: 'Published', value: 'Published' },
    { key: 'Overdue', value: 'Overdue' },
    { key: 'Deleted', value: 'Deleted' },
    { key: 'Archived', value: 'Archived' },
    { key: 'Under Review', value: 'Under Review' }
  ];

  // Map users to the required format for select options
  const userOptions = users.map(user => ({
    key: `${user.firstName} ${user.lastName}`,
    value: user.id
  }));

  // Create lookup functions for option labels
  const getLabel = (value: string, options: Array<{key: string; value: string}>) => {
    if (!value) return '';
    const option = options?.find(opt => opt.value === value);
    return option ? option.key : value;
  };

  return [
    // Document Information
    {
      key: 'documentType',
      label: 'Document Type',
      section: 'Document Information',
      type: 'select',
      options: normalizeOptions(documentTypes),
      getOptionLabel: (value) => getLabel(value, documentTypes)
    },
    {
      key: 'status',
      label: 'Status',
      section: 'Document Information',
      type: 'select',
      options: normalizeOptions(statuses),
      getOptionLabel: (value) => getLabel(value, statuses)
    },
    {
      key: 'uploadedBy',
      label: 'Owner',
      section: 'Document Information',
      type: 'singleuser',
      options: userOptions,
      metadata: {
        allowMissingUser: true,
      },
    },
    { 
      key: 'lastReviewDate',
      label: 'Last Review Date',
      section: 'Document Information',
      type: 'date'
    },
    {
      key: 'nextReviewDate',
      label: 'Next Review Date',
      section: 'Document Information',
      type: 'date'
    },

    // Classification & References
    {
      key: 'classification',
      label: 'Data Classification',
      section: 'Classification & References',
      type: 'select',
      options: normalizeOptions(classifications),
      getOptionLabel: (value) => getLabel(value, classifications)
    },
    {
      key: 'businessUnit',
      label: 'Business Unit',
      section: 'Classification & References',
      type: 'select',
      options: normalizeOptions(businessUnits),
      getOptionLabel: (value) => getLabel(value, businessUnits)
    },
    {
      key: 'procRef',
      label: 'Related Controls',
      section: 'Classification & References',
      type: 'controlcombobox',
      fullWidth: false
    },

    // Document Content
    {
      key: 'comments',
      label: 'Comments',
      section: 'Document Content',
      type: 'textarea',
      fullWidth: false
    },
    {
      key: 'fileName',
      label: 'File Name',
      section: 'Document Content',
      type: 'text',
      readOnly: true
    },
    {
      key: 'docHash',
      label: 'Document Hash',
      section: 'Document Content',
      type: 'text',
      readOnly: true
    },
    {
      key: 'confluenceURL',
      label: 'Confluence URL',
      section: 'Document Content',
      type: 'url'
    },

    // Metadata
    {
      key: 'componentId',
      label: 'Component ID',
      section: 'Document Metadata',
      type: 'text',
      readOnly: true
    },
    {
      key: 'dateUploaded',
      label: 'Date Uploaded',
      section: 'Document Metadata',
      type: 'date',
      readOnly: true
    },
    {
      key: 'linkedFromTaskId',
      label: 'Linked from Task',
      section: 'Document Metadata',
      type: 'text',
      readOnly: true
    }
  ];
}

// For backward compatibility
export const documentFieldsConfig = generateFieldConfig;